/**
 * AppNav — Navigation responsive
 * Portrait mobile  : icônes seules, 1 ligne, scroll horizontal si débordement
 * Paysage + tablette : icônes + labels sur 1 ligne
 * Desktop (≥900px) : icônes + labels, espacé
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, Upload, PlusCircle, User, Shield, MessageSquare } from 'lucide-react';
import HorseIcon from './HorseIcon';
import { isGuestSignupRole } from '../utils/trusteePermissions';

// i18n : labelKey/shortKey résolus au RENDU via t() (le tableau est module-level,
// résoudre ici figerait la langue à l'import — cf. CHART_LAYOUT pdfCharts).
const BASE_ITEMS = [
  { key: 'list',     labelKey: 'nav.items.list',     shortKey: 'nav.items.list',          Icon: BarChart3 },
  { key: 'upload',   labelKey: 'nav.items.upload',   shortKey: 'nav.items.uploadShort',   Icon: Upload },
  { key: 'horses',   labelKey: 'nav.items.horses',   shortKey: 'nav.items.horses',        Icon: HorseIcon },
  { key: 'newHorse', labelKey: 'nav.items.newHorse', shortKey: 'nav.items.newHorse',      Icon: PlusCircle },
  { key: 'profile',  labelKey: 'nav.items.profile',  shortKey: 'nav.items.profile',       Icon: User },
  { key: 'feedback', labelKey: 'nav.items.feedback', shortKey: 'nav.items.feedbackShort', Icon: MessageSquare },
];

const ADMIN_ITEM = { key: 'admin', labelKey: 'nav.items.admin', shortKey: 'nav.items.admin', Icon: Shield };

const AppNav = ({ currentPage, darkMode, onNavigate, currentUser }) => {
  const { t } = useTranslation();
  const isActive = (key) => currentPage === key;
  const isAdmin  = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';
  const hasGuestSignupRole = isGuestSignupRole(currentUser);

  const baseItems = hasGuestSignupRole
    ? BASE_ITEMS.filter(i => i.key !== 'newHorse' && i.key !== 'upload')
    : BASE_ITEMS;
  const items = isAdmin ? [...baseItems, ADMIN_ITEM] : baseItems;

  return (
    <nav aria-label={t('nav.aria.main')}>
      <style>{`
        .eky-nav {
          display: flex;
          gap: 4px;
          padding: 4px 8px 8px;
          justify-content: center;
          /* Portrait mobile : scroll horizontal si ça déborde */
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          flex-wrap: nowrap;
        }
        .eky-nav::-webkit-scrollbar { display: none; }

        .eky-btn {
          font-family: 'Inter', sans-serif;
          background: rgba(255,255,255,0.2);
          color: #fff;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0;
          transition: background 0.15s, box-shadow 0.15s;
          flex-shrink: 0;
          white-space: nowrap;

          /* Portrait mobile : carré compact avec label */
          flex-direction: column;
          font-size: 9px;
          padding: 7px 6px 5px;
          min-width: 52px;
          height: 50px;
          gap: 3px;
        }
        .eky-btn.active {
          background: rgba(255,255,255,0.95);
          color: #1565C0;
          font-weight: 700;
          box-shadow: 0 2px 8px rgba(0,0,0,0.18);
        }
        .eky-label {
          display: block;
          font-size: 10px;
          line-height: 1;
          letter-spacing: -0.01em;
        }
        /* Mobile portrait : label court */
        .eky-label-full { display: none; }
        .eky-label-short { display: block; }

        /* Paysage mobile et petites tablettes */
        @media (min-width: 480px), (orientation: landscape) {
          .eky-label-full { display: block; }
          .eky-label-short { display: none; }
          .eky-nav {
            flex-wrap: nowrap;
            justify-content: center;
          }
          .eky-btn {
            flex-direction: row;
            gap: 5px;
            font-size: 11px;
            padding: 6px 10px;
            height: 36px;
            min-width: unset;
          }
          .eky-label { font-size: 11px; }
        }

        /* Tablette et desktop */
        @media (min-width: 768px) {
          .eky-nav { padding: 4px 12px 10px; gap: 6px; }
          .eky-btn {
            padding: 7px 14px;
            height: 38px;
            font-size: 12px;
            gap: 6px;
          }
          .eky-label { font-size: 12px; }
        }
      `}</style>

      <div className="eky-nav">
        {items.map(({ key, labelKey, shortKey, Icon }) => (
          <button
            key={key}
            onClick={() => onNavigate(key)}
            className={`eky-btn${isActive(key) ? ' active' : ''}`}
            aria-label={t(labelKey)}
            aria-current={isActive(key) ? 'page' : undefined}
          >
            {key === 'horses'
              ? <Icon size={18} />
              : <Icon size={16} strokeWidth={2.2} />}
            <span className="eky-label eky-label-short">{t(shortKey)}</span>
            <span className="eky-label eky-label-full">{t(labelKey)}</span>
          </button>
        ))}
      </div>
    </nav>
  );
};

export default AppNav;
